import { useState } from 'react';
import { CircleAlert, Download } from 'lucide-react';

import { downloadUrl } from '@/api/client';
import { useDeadStock } from '@/api/hooks/useAnalytics';
import { Can } from '@/auth/guards';
import { PERMISSIONS } from '@/auth/permissions';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { money } from '@/lib/utils';

export function DeadStockTab() {
  const [days, setDays] = useState(180);
  const { data, isLoading } = useDeadStock(days);

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Label htmlFor="dead-days" className="text-xs">
            No sale in
          </Label>
          <Input
            id="dead-days"
            type="number"
            min={1}
            value={days}
            onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 180))}
            className="h-8 w-20"
          />
          <span className="text-xs text-muted-foreground">days</span>
        </div>
        <Can permission={PERMISSIONS.exportsCreate}>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href={downloadUrl('/api/export/stock-logs')}>
                <Download /> XLSX
              </a>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href={downloadUrl('/api/export/stock-logs/csv')}>
                <Download /> CSV
              </a>
            </Button>
          </div>
        </Can>
      </div>

      <Card className="border-status-low/30 bg-status-low/5">
        <CardContent className="flex flex-wrap items-center gap-6 py-5">
          <div className="flex items-center gap-3">
            <CircleAlert className="h-8 w-8 shrink-0 text-status-low" />
            <div>
              <p className="tabular text-2xl font-bold">{money(data.totalRetailValue)}</p>
              <p className="text-xs text-muted-foreground">
                locked in {data.count} medicine(s), {data.totalUnits} unit(s)
              </p>
            </div>
          </div>
          {data.potentialLoss != null && (
            <div>
              <p className="tabular text-lg font-semibold text-status-expired">{money(data.potentialLoss)}</p>
              <p className="text-xs text-muted-foreground">potential margin at risk</p>
            </div>
          )}
        </CardContent>
      </Card>

      {Object.keys(data.byCategory).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">By category</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-2 sm:grid-cols-2">
            {Object.entries(data.byCategory)
              .sort(([, a], [, b]) => b.retailValue - a.retailValue)
              .map(([category, breakdown]) => (
                <div key={category} className="flex items-center justify-between rounded-md bg-secondary/50 px-3 py-2 text-sm">
                  <span>{category}</span>
                  <span className="text-right">
                    <span className="tabular block font-medium">{money(breakdown.retailValue)}</span>
                    <span className="text-xs text-muted-foreground">{breakdown.count} item(s)</span>
                  </span>
                </div>
              ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Items</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {data.items.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              Nothing has gone stagnant — every in-stock item has sold within {days} days.
            </p>
          ) : (
            <div className="divide-y divide-border">
              {data.items.map((item) => (
                <div key={item.id} className="flex items-center gap-3 px-5 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{item.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.stock} {item.unit} · {item.category}
                    </p>
                  </div>
                  <span className="tabular shrink-0 text-sm font-semibold">{money(item.retailValue)}</span>
                </div>
              ))}
              {data.truncated && (
                <p className="px-5 py-3 text-center text-xs text-muted-foreground">
                  Showing the top 100 by value. Export for the full list.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}